// Stub data layer for the In Action redesign mock. Real backend wiring
// replaces these with: source-text snapshot column on InActionList, per-word
// sourceSentence column on InActionWord (B.4), and a list-progress endpoint.

import { InActionListHistory, InActionWord } from 'app/types';

export interface MockListProgress {
  // Words that are now LEARNED_ACTIVE among the addressable groups
  // (excludes "Потребують уваги" since those can't be drilled).
  processed: number;
  addressable: number;
  pct: number;
}

export interface MockLastSession {
  sessionId: number;
  startedAt: string; // ISO
  itemsDone: number;
  itemsTotal: number;
}

// Demo source text — pretend a user pasted a Game of Thrones transcript.
// Real BE would store this on InActionList.sourceText (new column).
export const MOCK_SOURCE_TEXT = `Lord Stark spoke quietly to his daughter at the edge of the godswood. The wind was sharp and the ravens circled overhead, ominous against a pale sky. He looked up at the weirwood, weighed his words, and at last said what had been gnawing at him for days. "You will need courage now," he murmured, "courage and patience both." Arya stared at the ground, jaw clenched. She did not yet understand the weight of inheritance, the slow grinding pressure of a name that was older than the kingdom itself. Outside the walls a battle was beginning that none of them would survive untouched.`;

const FALLBACK_SENTENCES = MOCK_SOURCE_TEXT.split(/(?<=[.!?])\s+/);

// Pick a sentence that visually "contains" a word. We don't actually need
// semantic correctness for the mock — only that the word renders highlighted.
export const mockSentenceForWord = (word: InActionWord): string => {
  const label = (word.word || '').toLowerCase();
  if (!label) return FALLBACK_SENTENCES[0];
  const match = FALLBACK_SENTENCES.find((s) =>
    s.toLowerCase().includes(label),
  );
  if (match) return match;
  // Fallback: deterministically pick by id so it doesn't jitter on re-render.
  const idx = (word.id ?? 0) % FALLBACK_SENTENCES.length;
  return FALLBACK_SENTENCES[idx];
};

// "Опрацьовано з цього списку" — counts LEARNED_ACTIVE words against the
// addressable group (anything we could drill in an exercise). When the BE
// adds a real progress endpoint, replace with that.
export const computeListProgress = (
  words: InActionWord[],
): MockListProgress => {
  const addressable = words.filter(
    (w) =>
      w.analysisGroup !== 'NOT_IN_VOCABULARY' ||
      // "Нові" (has translation) is still addressable through the dictionary path.
      !!w.dictionaryId ||
      !!w.dictionaryIrregularVerbId,
  ).length;
  const processed = words.filter(
    (w) => w.analysisGroup === 'LEARNED_ACTIVE',
  ).length;
  const pct = addressable
    ? Math.round((processed / addressable) * 100)
    : 0;
  return { processed, addressable, pct };
};

// Mock "last session from this list". Real implementation: query
// LearningSession WHERE source_in_action_list_id = listId ORDER BY createdAt DESC.
export const mockLastSession = (listId: string | number): MockLastSession => ({
  sessionId: Number(listId) * 100 + 7,
  startedAt: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(),
  itemsDone: 9,
  itemsTotal: 12,
});

// Source-type icon hint for the history items. Real impl: derive from
// InActionList.sourceType + future sourceMeta.
export const mockSourceKindLabel = (
  sourceType: InActionListHistory['sourceType'],
): { label: string; icon: 'text' | 'file' | 'subs' | 'url' } => {
  if (sourceType === 'TXT_FILE') return { label: '.txt файл', icon: 'file' };
  return { label: 'Вставлений текст', icon: 'text' };
};

// Mock how many of a list's words the user has already moved out of "new" —
// used for the per-history-item progress chip.
export const mockHistoryItemProgress = (
  item: InActionListHistory,
): { processed: number; pct: number } => {
  // Deterministic so it doesn't jitter: seed off id.
  const seed = (item.id * 9301 + 49297) % 233280;
  const r = seed / 233280;
  const processed = Math.floor(r * Math.min(item.wordsCount, 40));
  const pct = item.wordsCount
    ? Math.round((processed / item.wordsCount) * 100)
    : 0;
  return { processed, pct };
};

// Deterministic 5-group split per list. The BE doesn't expose composition on
// InActionListHistory yet — derive a stable mock from the id so the bar reads
// the same on every reload. Replace with a real `comp` field when it lands.
export const mockHistoryItemComposition = (
  item: InActionListHistory,
): {
  learned: number;
  repeat: number;
  notLearned: number;
  new: number;
  attention: number;
} => {
  const total = Math.max(item.wordsCount, 0);
  if (!total) {
    return { learned: 0, repeat: 0, notLearned: 0, new: 0, attention: 0 };
  }
  // Mulberry32-ish noise off id; weights roughly mirror a typical analysis
  // (most learned, then new/notLearned, fewer repeat/attention).
  const rand = (k: number) => {
    let s = (item.id * 73 + k * 9301 + 49297) >>> 0;
    s = Math.imul(s ^ (s >>> 15), s | 1);
    s ^= s + Math.imul(s ^ (s >>> 7), s | 61);
    return ((s ^ (s >>> 14)) >>> 0) / 4294967296;
  };
  const weights = [
    0.42 + rand(1) * 0.12, // learned
    0.1 + rand(2) * 0.08, // repeat
    0.12 + rand(3) * 0.08, // notLearned
    0.15 + rand(4) * 0.1, // new
    0.04 + rand(5) * 0.05, // attention
  ];
  const wSum = weights.reduce((a, b) => a + b, 0);
  const raw = weights.map((w) => (w / wSum) * total);
  const ints = raw.map((v) => Math.floor(v));
  // Distribute the remainder to the buckets with the largest fractional parts
  // so the segment sum exactly matches wordsCount.
  const remainder = total - ints.reduce((a, b) => a + b, 0);
  const order = raw
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac);
  for (let i = 0; i < remainder && i < order.length; i += 1) {
    ints[order[i].i] += 1;
  }
  return {
    learned: ints[0],
    repeat: ints[1],
    notLearned: ints[2],
    new: ints[3],
    attention: ints[4],
  };
};
